export type Point = [number, number];
export interface Component { cells: number; rings: Point[][] }

/**
 * Trace the edges between matched and unmatched cells and chain them into rings.
 * Exact on the sample grid — no smoothing — and signed area separates holes from
 * exteriors, so the output is valid GeoJSON rather than a pile of loose rings.
 *
 * @param minCells drop specks smaller than this
 */
export function maskToRings(
  mask: Uint8Array, w: number, h: number, minCells: number,
): Component[] {
  const seen = new Uint8Array(w * h);
  const comps: Point[][] = [];
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const i = y * w + x;
    if (!mask[i] || seen[i]) continue;
    const stack: Point[] = [[x, y]];
    const cells: Point[] = [];
    seen[i] = 1;
    while (stack.length) {
      const [cx, cy] = stack.pop()!;
      cells.push([cx, cy]);
      ([[1, 0], [-1, 0], [0, 1], [0, -1]] as Point[]).forEach(([dx, dy]) => {
        const nx = cx + dx, ny = cy + dy, ni = ny * w + nx;
        if (nx >= 0 && ny >= 0 && nx < w && ny < h && mask[ni] && !seen[ni]) {
          seen[ni] = 1; stack.push([nx, ny]);
        }
      });
    }
    if (cells.length >= minCells) comps.push(cells);
  }

  return comps.map((cells) => {
    const inComp = new Set(cells.map(([x, y]) => y * w + x));
    // Bounds-check x: without it y*w+x wraps a column, so has(w, 0) reports the first
    // cell of the next row and every region touching a grid edge loses that side.
    const has = (x: number, y: number) =>
      x >= 0 && y >= 0 && x < w && y < h && inComp.has(y * w + x);

    const edges = new Map<string, Point>();
    const add = (a: Point, b: Point) => edges.set(a.join(','), b);
    for (const [x, y] of cells) {
      if (!has(x, y - 1)) add([x, y], [x + 1, y]);
      if (!has(x + 1, y)) add([x + 1, y], [x + 1, y + 1]);
      if (!has(x, y + 1)) add([x + 1, y + 1], [x, y + 1]);
      if (!has(x - 1, y)) add([x, y + 1], [x, y]);
    }
    const rings: Point[][] = [];
    while (edges.size) {
      const startKey = edges.keys().next().value as string;
      let cur = startKey.split(',').map(Number) as Point;
      const ring: Point[] = [cur];
      for (;;) {
        const k = cur.join(',');
        const nxt = edges.get(k);
        if (!nxt) break;
        edges.delete(k);
        cur = nxt;
        ring.push(cur);
        if (cur[0] === ring[0]![0] && cur[1] === ring[0]![1]) break;
      }
      if (ring.length > 3) rings.push(simplifyRing(ring));
    }
    return { cells: cells.length, rings };
  });
}

/** Shoelace. Sign distinguishes an exterior ring from a hole. */
export function ringArea(r: Point[]): number {
  let a = 0;
  for (let i = 0; i < r.length - 1; i++) {
    a += r[i]![0] * r[i + 1]![1] - r[i + 1]![0] * r[i]![1];
  }
  return a / 2;
}

/** Drop collinear points, keeping only corners. */
export function simplifyRing(r: Point[]): Point[] {
  const out: Point[] = [r[0]!];
  for (let i = 1; i < r.length - 1; i++) {
    const [ax, ay] = out[out.length - 1]!;
    const [bx, by] = r[i]!;
    const [cx, cy] = r[i + 1]!;
    if ((bx - ax) * (cy - by) !== (by - ay) * (cx - bx)) out.push(r[i]!);
  }
  out.push(r[r.length - 1]!);
  return out;
}
